'use client';

import { ChevronRight, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import { CardWithMovingBorder } from '@/components/shared/m-card-with-moving-border';
import { Skeleton } from '@/components/shared/skeleton';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import { useLaunchGame } from '@/features/games/hooks/useLaunchGame';
import { useCurrency } from '@/hooks/useCurrency';
import { useLanguage } from '@/hooks/useLanguage';

const RECOMMENDATION_GAMES = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  label: 'Bigger Bass',
  imageUrl: `/games/bigger-bass.png`,
  onClick: () => {
    console.log('Recommended Game', i);
  },
}));
const RECOMMENDATION_LIVE_CASINO = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  label: 'Blackjack',
  imageUrl: `/games/blackjack.png`,
  onClick: () => {
    console.log('Recommended Game', i);
  },
}));

const RECOMMENDATION_SLOT = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  label: 'Slot ocean',
  imageUrl: `/games/slot-ocean.png`,
  onClick: () => {
    console.log('Recommended Game', i);
  },
}));
const RECOMMENDATION_FISHING = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  label: 'Jackpot fishing',
  imageUrl: `/games/jackpot-fishing.png`,
  onClick: () => {
    console.log('Recommended Game', i);
  },
}));
const RECOMMENDATION_MINI_GAMES = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  label: 'Jogo',
  imageUrl: `/games/jogodobicho.png`,
  onClick: () => {
    console.log('Recommended Game', i);
  },
}));
const RECOMMENDATION_POKER = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  label: 'Poker king',
  imageUrl: `/games/poker-king.png`,
  onClick: () => {
    console.log('Recommended Game', i);
  },
}));
const RECOMMENDATION_MAHJONG = Array.from({ length: 12 }, (_, i) => ({
  id: i,
  label: 'Baccarat 10',
  imageUrl: `/games/baccarat.png`,
  onClick: () => {
    console.log('Recommended Game', i);
  },
}));

interface RecommendationGamesProps {
  games?: Array<{
    id: string;
    name: string;
    image?: string | null;
    isFavorite?: boolean;
  }>;
  isLoading?: boolean;
}

function RecommendationGamesInner({
  games,
  isLoading,
}: RecommendationGamesProps) {
  const searchParams = useSearchParams();
  const paramActive = searchParams.get('active') || 'games';

  const launch = useLaunchGame();
  const { locale } = useLanguage();
  const { currencyCode } = useCurrency();

  const play = (gameId: string) => {
    launch.mutate({
      gameId,
      currency: currencyCode ?? undefined,
      language: locale,
      isMobile: true,
    });
  };

  const getRecommendationSlides = () => {
    const list = games
      ? games.slice(0, 6)
      : (() => {
          switch (paramActive) {
            case 'games':
              return RECOMMENDATION_GAMES.slice(0, 6);
            case 'slot':
              return RECOMMENDATION_SLOT.slice(0, 6);
            case 'fishing':
              return RECOMMENDATION_FISHING.slice(0, 6);
            case 'mini-games':
              return RECOMMENDATION_MINI_GAMES.slice(0, 6);
            case 'poker':
              return RECOMMENDATION_POKER.slice(0, 6);
            case 'mahjong':
              return RECOMMENDATION_MAHJONG.slice(0, 6);
            case 'live-casino':
              return RECOMMENDATION_LIVE_CASINO.slice(0, 6);
            default:
              return RECOMMENDATION_GAMES.slice(0, 6);
          }
        })();

    return list.map(game => {
      if ('name' in game) {
        return {
          id: String(game.id),
          label: game.name,
          imageUrl: game.image || undefined,
          onClick: () => play(String(game.id)),
        };
      }
      return {
        id: String(game.id),
        label: game.label,
        imageUrl: game.imageUrl || undefined,
        onClick: () => play(String(game.id)),
      };
    });
  };

  const slides = getRecommendationSlides();

  if (isLoading) {
    return (
      <div className="relative p-4 w-full">
        <div className="w-full flex items-center justify-between">
          <h4 className="font-bold text-sm text-[#FFA035]">
            Recommended Games
          </h4>
          <Link
            href="/pages/m-more-games?type=recommended"
            className="text-[10px] text-white flex items-center gap-1 cursor-pointer hover:opacity-80 active:opacity-70"
          >
            more games <ChevronRight className="size-3" />
          </Link>
        </div>
        <div className="-mx-4 px-4 py-4 flex gap-3 overflow-hidden">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders
              key={`skeleton-${idx}`}
              className="basis-[192px] shrink-0 h-[150px] p-[2px] rounded-lg border border-white/5 bg-[#172554]/40 flex flex-col gap-2 p-1.5 animate-pulse"
            >
              <Skeleton className="flex-1 rounded-md" />
              <div className="flex items-center justify-between gap-2 mt-auto">
                <Skeleton className="h-4 w-20 rounded" />
                <Skeleton className="h-6 w-16 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (games && slides.length === 0) {
    return null;
  }

  return (
    <div className="relative p-4 w-full">
      <div className="w-full flex items-center justify-between">
        <h4 className="font-bold text-sm text-[#FFA035]">Recommended Games</h4>
        <Link
          href="/pages/m-more-games?type=recommended"
          className="text-[10px] text-white flex items-center gap-1  cursor-pointer hover:opacity-80 active:opacity-70"
        >
          more games <ChevronRight className="size-3" />
        </Link>
      </div>

      <div className="-mx-4 px-4 py-4">
        <Carousel
          opts={{
            align: 'start',
            loop: slides.length > 1,
            containScroll: 'trimSnaps',
            dragFree: false,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-3">
            {slides.map(({ id, label, imageUrl, onClick }, idx) => (
              <CarouselItem
                // biome-ignore lint/suspicious/noArrayIndexKey: carousel item key
                key={`${id}-${idx}`}
                className="pl-3 basis-[192px]"
              >
                <CardWithMovingBorder
                  imageUrl={imageUrl}
                  title={label}
                  onClick={onClick}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

      {launch.isPending && (
        <div className="absolute inset-0 flex items-center justify-center rounded-sm bg-black/55 z-50">
          <Loader2 className="size-8 animate-spin text-white" />
        </div>
      )}
    </div>
  );
}

export const RecommendationGames = ({
  games,
  isLoading,
}: RecommendationGamesProps) => (
  <Suspense
    fallback={
      <div className="relative p-4 w-full" aria-hidden>
        <div className="w-full flex items-center justify-between">
          <h4 className="font-bold text-sm text-[#FFA035]">
            Recommended Games
          </h4>
          <div className="text-[10px] text-white flex items-center gap-1 opacity-50">
            more games <ChevronRight className="size-3" />
          </div>
        </div>
        <div className="-mx-4 px-4 py-4 flex gap-3 overflow-hidden">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders
              key={`fallback-skeleton-${idx}`}
              className="basis-[192px] shrink-0 h-[150px] p-[2px] rounded-lg border border-white/5 bg-[#172554]/40 flex flex-col gap-2 p-1.5 animate-pulse"
            >
              <Skeleton className="flex-1 rounded-md" />
              <div className="flex items-center justify-between gap-2 mt-auto">
                <Skeleton className="h-4 w-20 rounded" />
                <Skeleton className="h-6 w-16 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      </div>
    }
  >
    <RecommendationGamesInner games={games} isLoading={isLoading} />
  </Suspense>
);
